// The reveal (WI-13; mockups docs/renders/rewards3d rw01–rw03): the twist opens straight onto ONE
// reward, already decided and recorded (useArcade `twist`). The machine (B16) draws its own opening
// through its `Reveal` hook; this screen adds the words and what to do next:
//  - a collectible: its name, the island set's progress, gold at three of a kind; onto the shelf;
//  - an act key: "New on <island>: <act>!" and Go and see (flies there and plays it) or Later;
//  - a real prize: as before, a grown-up cashes it in (a grown-up player's is theirs at once).
import { motion } from 'motion/react'
import { useEffect, useId, useMemo, useRef } from 'react'
import type { Pack } from '../../shared/pack/schema'
import { buzz } from '../audio/haptics'
import { closeOverlay, goTab, replaceOverlay } from '../app/router'
import { useModal } from '../app/Sheet'
import { useEvents } from '../data/progress'
import { collectibleSet, setOf, TRAVELLER_SET } from '../domain/collectibles'
import { GOLD_AT, ownedCollectibles } from '../domain/release'
import { PLACES } from '../map/places/registry'
import { springs } from '../motion/springs'
import { useMotionTier } from '../motion/tier'
import { useStage } from '../stage/bridge'
import { t } from '../strings/en'
import { useSession } from '../data/session'
import { grownUpPlayer } from '../domain/players'
import { useRewardMachine } from './machines'
import type { RevealLook } from './machines/types'
import { useGrownUpName } from './PlaysSheet'
import { useTwist, type TwistDone } from './useArcade'

const CONFETTI = ['#fff', '#2F7BFF', '#FF4F4F', '#36D399', '#fff', '#FFC93C', '#2F7BFF', '#fff', '#FF4F4F', '#36D399', '#fff', '#2F7BFF']
const RARITY_TAG: Record<string, string> = { common: 'tag', rare: 'tag blue', legendary: 'tag yellow' }
const RARITY_COLOUR: Record<string, string> = { common: '#FF4F4F', rare: '#2F7BFF', legendary: '#FFC93C' }

export const islandTitle = (id: string): string => (id === TRAVELLER_SET ? 'Traveller' : PLACES.find((p) => p.id === id)?.title ?? id)

/** What the machine opens onto, for its Reveal hook. */
export function revealLook(done: TwistDone, owned: ReadonlyMap<string, number>): RevealLook {
  const p = done.payout
  if (p.kind === 'key') return { kind: 'key', emoji: '🔑', colour: '#9B6BFF' }
  if (p.kind === 'prize') return { kind: 'prize', emoji: p.prize.emoji, colour: RARITY_COLOUR[p.prize.rarity] ?? '#FF4F4F' }
  const n = Math.max(owned.get(p.item.id) ?? 0, p.count)
  return { kind: 'collectible', emoji: p.item.emoji, ...(p.item.art ? { art: p.item.art } : {}), colour: '#36D399', gold: n >= GOLD_AT }
}

export function Reward({ pack, personId }: { pack: Pack; personId: string }) {
  void pack
  const done = useTwist((s) => (s.last?.personId === personId ? s.last : null))
  const events = useEvents()
  const tier = useMotionTier()
  const still = tier === 'still'
  const calm = tier !== 'full'
  const machine = useRewardMachine()
  const grownUp = useGrownUpName(personId)
  const me = useSession((s) => s.people?.find((p) => p.id === personId))
  const self = !!me && grownUpPlayer(me)
  const owned = useMemo(() => ownedCollectibles(events, personId), [events, personId])
  const look = useMemo(() => (done ? revealLook(done, owned) : null), [done, owned])
  const ref = useRef<HTMLDivElement>(null)
  const headId = useId()
  useModal(ref, { active: !!done })

  const reveal = machine.sounds.reveal
  const kind = done?.payout.kind
  const rarity = done?.payout.kind === 'prize' ? done.payout.prize.rarity : undefined
  useEffect(() => {
    if (!done) {
      closeOverlay()
      return
    }
    setTimeout(() => buzz('slap'), still ? 0 : 140)
    if (!calm && kind) reveal(kind, rarity)
    // Once per twist.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done?.twistId])

  if (!done || !look) return null
  const p = done.payout
  const variant = p.kind === 'prize' ? p.prize.rarity : p.kind

  let body: React.ReactNode
  let foot: React.ReactNode
  if (p.kind === 'collectible') {
    const set = collectibleSet(setOf(p.item.id))
    const have = set.filter((c) => (owned.get(c.id) ?? 0) > 0 || c.id === p.item.id).length
    const tag = look.gold && p.count === GOLD_AT ? t.reward.goldTag : p.count > 1 ? t.reward.againTag(p.count) : t.reward.newTag
    const title = islandTitle(setOf(p.item.id))
    body = (
      <>
        <span className={`tag ${look.gold ? 'yellow' : 'mint'}`}>{tag}</span>
        <h2 id={headId}>{p.item.name}!</h2>
        <p>{t.reward.setLine(title, have, set.length)}</p>
        <div className="set-dots" aria-hidden>
          {set.map((c) => (
            <i key={c.id} className={c.id === p.item.id ? 'new' : (owned.get(c.id) ?? 0) > 0 ? 'got' : ''} />
          ))}
        </div>
        {p.completes ? <p className="set-done">{t.reward.setDone(title)}</p> : null}
      </>
    )
    foot = (
      <>
        <p>{t.reward.goldHint}</p>
        <button className="btn y press" onClick={() => closeOverlay()}>
          {t.reward.shelf}
        </button>
      </>
    )
  } else if (p.kind === 'key') {
    body = (
      <>
        <span className="tag purple">{t.reward.keyTag(islandTitle(p.place))}</span>
        <h2 id={headId}>{t.reward.keyTitle(p.label)}</h2>
        <p>{t.reward.keySub}</p>
      </>
    )
    foot = (
      <>
        <button
          className="btn press"
          onClick={() => {
            goTab('map')
            const s = useStage.getState()
            s.set({ goIsland: { id: p.place, n: (s.goIsland?.n ?? 0) + 1 }, forceAct: { id: p.act, n: (s.forceAct?.n ?? 0) + 1 } })
          }}
        >
          {t.reward.go}
        </button>
        <button className="btn w press" onClick={() => closeOverlay()}>
          {t.reward.later}
        </button>
      </>
    )
  } else {
    const real = p.prize.kind === 'real' && !self
    body = (
      <>
        <span className={RARITY_TAG[p.prize.rarity]}>{t.prizeReveal.rarity[p.prize.rarity]}</span>
        <h2 id={headId}>{p.prize.title}!</h2>
        <p>{real ? t.prizeReveal.real : self && p.prize.kind === 'real' ? t.prizeReveal.realSelf : t.prizeReveal.instant}</p>
      </>
    )
    foot = (
      <>
        {real ? (
          <div className="panel prize-note">
            <span aria-hidden>👋</span>
            <div>
              <b>{t.prizeReveal.noteTitle(grownUp)}</b>
              <small>{t.prizeReveal.noteSub}</small>
            </div>
          </div>
        ) : null}
        <button className="btn press" onClick={() => replaceOverlay('my-prizes')}>
          {t.prizeReveal.into}
        </button>
      </>
    )
  }

  return (
    <div ref={ref} className={`reward prize-reveal reveal-${variant}`} role="dialog" aria-modal="true" tabIndex={-1} aria-labelledby={headId} data-payout={p.kind}>
      <div className={`rays${calm ? ' paused' : ''}`} aria-hidden />
      {!calm ? (
        <div className="confetti" aria-hidden>
          {CONFETTI.map((c, i) => (
            <i key={i} style={{ background: c, left: `${6 + i * 8}%`, animationDelay: `${0.22 + (i % 4) * 0.06}s` }} />
          ))}
        </div>
      ) : null}
      <machine.Reveal look={look} still={still} calm={calm} />
      {/* The words are never faded out: a slide up only, so a paused or throttled animation (a
          background tab, a fixed test clock, a slow phone) can't leave the reveal without its heading. */}
      <motion.div className="reward-text" initial={still ? false : { y: 16 }} animate={{ y: 0 }} transition={{ delay: still ? 0 : 0.35, ...springs.pop }}>
        {body}
      </motion.div>
      <div className="reward-foot">{foot}</div>
    </div>
  )
}
